import { type ButtonProps, Dropdown, Typography } from "antd";
import { type BTFileSystem } from "legacyComponents/FileUploadContainer.types";
import { type FunctionComponent, useMemo, useState } from "react";

import { type MediaType } from "types/enum";

import { getClosestModal } from "utilities/helpers";
import { type KeyOfOrString } from "utilities/type/PropsOfType";

import { BTButtonDeprecated } from "commonComponents/btWrappers/BTButtonDeprecated/BTButtonDeprecated";
import { BTIconCaretDownOutlined } from "commonComponents/btWrappers/BTIcon";
import { BrowseBTFiles } from "commonComponents/entity/media/BrowseBTFiles/BrowseBTFiles";
import { type BrowseBTFilesHandler } from "commonComponents/entity/media/BrowseBTFiles/BrowseBTFiles.api.handler";

import "./BrowseBTFilesDropdown.less";

export interface IBrowseBTFilesDropdownProps<FormValues = undefined> {
    id: KeyOfOrString<FormValues>;
    handler?: BrowseBTFilesHandler;
    allowedMediaTypes?: MediaType[];
    jobId?: number;
    disabled?: boolean;
    "data-testid": string;
    size?: ButtonProps["size"];
    allowedFileTypes: string[];

    /** Pass setFieldTouched -- DO NOT PASS handleBlur */
    onBlur: (field: string, isTouched: boolean) => void;

    onFileSelected: (file: BTFileSystem) => Promise<void>;
}

export function BrowseBTFilesDropdown<FormValues>({
    id,
    jobId,
    disabled,
    size,
    "data-testid": dataTestId,
    allowedMediaTypes,
    allowedFileTypes,
    handler,
    onBlur,
    onFileSelected,
}: IBrowseBTFilesDropdownProps<FormValues>): ReturnType<
    FunctionComponent<IBrowseBTFilesDropdownProps<FormValues>>
> {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState<BTFileSystem | null>(null);

    const handleVisibleChange = (visible: boolean) => {
        setIsDropdownOpen(visible);
    };

    const handleBlur = () => {
        onBlur(id.toString(), true);
    };

    const handleCancel = () => {
        setIsDropdownOpen(false);
    };

    const handleSubmit = async (file: BTFileSystem[]) => {
        await onFileSelected(file[0]);
        setIsDropdownOpen(false);
    };

    const handleChange = async (files: BTFileSystem[]) => {
        setSelectedFile(files.length > 0 ? files[0] : null);
    };

    const memoAllowedMediaTypes = useMemo(() => allowedMediaTypes, [allowedMediaTypes]);

    const overlay = (
        <BrowseBTFiles
            handler={handler}
            allowedMediaTypes={memoAllowedMediaTypes}
            jobId={jobId}
            allowedFileTypes={allowedFileTypes}
            multiple={false}
            onCancel={handleCancel}
            onSubmit={handleSubmit}
            onChange={handleChange}
        />
    );
    return (
        // eslint-disable-next-line react/forbid-elements
        <Dropdown
            trigger={["click"]}
            onVisibleChange={handleVisibleChange}
            visible={isDropdownOpen}
            disabled={disabled}
            overlay={<div className="BrowseBTFilesDropdown">{overlay}</div>}
            getPopupContainer={getClosestModal}
            openClassName="uploaded-file-picker-open"
        >
            <BTButtonDeprecated
                id={id.toString()}
                data-testid={dataTestId}
                style={{ width: "100%", maxWidth: "85vw", textAlign: "left" }}
                size={size}
                onBlur={handleBlur}
            >
                <Typography.Text type="secondary">
                    {selectedFile ? selectedFile.title : "Select a File"}
                </Typography.Text>
                <span
                    className="ant-select-arrow"
                    unselectable="on"
                    style={{ userSelect: "none", position: "absolute" }}
                >
                    <BTIconCaretDownOutlined />
                </span>
            </BTButtonDeprecated>
        </Dropdown>
    );
}
